<script setup lang="ts">
import { ref } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import {
	between,
	required,
	titleInputRules,
} from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import OccupancyChart from "@/modules/booking_experts/pages/Components/SVG/OccupancyChart.vue";
import YoYChart from "@/modules/booking_experts/pages/Components/SVG/YoYChart.vue";
import CustomChart from "@/modules/booking_experts/pages/Components/SVG/CustomChart.vue";
import { createBezierCurve } from "@/modules/booking_experts/utils";

// Types
import PriceModelCurvePostDTO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurvePostDTO;
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import Modal from "@/ui/components/modal.vue";

const api = useApiClient("bookingExperts");

const visible = defineModel<boolean>();
const emit = defineEmits(["curve-created"]);

// State
const loading = ref(false);
const nameValid = ref(false);

const modelItem = ref<PriceModelCurvePostDTO>({
	days: 365,
	name: "",
	type: "custom",
	description: "",
	color: "#99d5e2",
	price_adjustment_type: "relative",
});

// Curve type definitions
const tabs = [
	{
		value: "occupancy",
		component: OccupancyChart,
		description: t("pricelist.curves.descriptions.occupancy"),
		readonly: true,
	},
	{
		value: "year_over_year",
		component: YoYChart,
		description: t("pricelist.curves.descriptions.year_over_year"),
		readonly: true,
	},
	{
		value: "custom",
		component: CustomChart,
		description: t("pricelist.curves.descriptions.custom"),
		readonly: false,
	},
] as const;

function resetModal() {
	modelItem.value = {
		days: 365,
		name: "",
		type: "custom",
		description: "",
		color: "#9AE299",
		price_adjustment_type: "relative",
	};
	nameValid.value = false;
}

// Close modal
function closeModal() {
	resetModal();
	visible.value = false;
}

// Create curve
async function handleSubmit() {
	loading.value = true;

	const result = await toastApiResult(
		api.createPriceModelCurve(modelItem.value)
	);

	loading.value = false;

	if (result.isOk()) {
		const newCurve = result.value;

		if (newCurve.type === "year_over_year") {
			newCurve.base_dataset = createBezierCurve(newCurve.days, 100);
			await api.updatePriceModelCurve(newCurve.id, newCurve);
		}

		emit("curve-created"); // Signal to parent to refresh curves
		resetModal();
		visible.value = false;
	}
}
</script>

<template>
	<Modal
		v-model:visible="visible"
		v-model:allow-submit="nameValid"
		title="Create Curve"
		icon="fa-light fa-chart-line"
		width="600px"
		@submit="handleSubmit"
	>
		<v-form v-model="nameValid">
			<div class="d-flex flex-column gap-4">
				<div class="d-flex flex-row ga-3 mb-3">
					<!-- Color picker -->
					<v-sheet
						color="background"
						height="48"
						width="48"
						class="d-flex align-center justify-center rounded-t"
						style="border-bottom: 1px solid #979797"
					>
						<color-picker v-model="modelItem.color" />
					</v-sheet>
					<!-- Name field -->
					<v-text-field
						v-model="modelItem.name"
						label="Curve Name"
						placeholder="e.g., Summer Season Model"
						hide-details="auto"
						:rules="titleInputRules(t)"
					>
					</v-text-field>
				</div>

				<!-- Description field -->
				<v-textarea
					v-model="modelItem.description"
					label="Description"
					rows="3"
					hide-details
					auto-grow
				/>
			</div>
			<v-divider class="mt-3 mb-2" />
			<h4>Curve Type</h4>
			<p class="mb-2">
				Select the type of pricing curve that best fits your business strategy.
			</p>

			<v-tabs
				color="secondary"
				bg-color="surface"
				v-model="modelItem.type"
				slider-color="primary"
				class="mb-2 rounded curve-tabs"
				centered
			>
				<v-tab
					v-for="item in tabs"
					:key="item.value"
					:value="item.value"
					class="curve-tab"
					variant="flat"
				>
					{{ t(`pricelist.curves.${item.value}`) }}
				</v-tab>
			</v-tabs>

			<!-- Curve preview -->
			<div
				class="curve-preview-container border rounded"
				style="background-color: white"
			>
				<component
					class="my-7"
					style="transform: scale(1.5); background-color: transparent"
					:is="tabs.find((t) => t.value === modelItem.type)?.component"
				/>
				<!-- Curve description -->
				<v-card variant="flat" color="background" class="rounded-t-0">
					<v-card-text>
						<p class="text-body-1">
							{{ tabs.find((t) => t.value === modelItem.type)?.description }}
						</p>
					</v-card-text>
				</v-card>
			</div>

			<!-- Additional settings specific to curve type -->
			<v-text-field
				v-if="modelItem.type === 'custom'"
				v-model.number="modelItem.days"
				label="Lead Days (2-730)"
				:rules="[required(t), between(t, 2, 730)]"
				type="number"
				min="2"
				max="730"
				hide-spin-buttons
				hide-details="auto"
				class="mt-3"
			/>
			<v-divider class="mt-3 mb-2" />
			<h4>Price Adjustment</h4>
			<p class="mb-2">Select the type of price adjustment</p>
			<v-tabs
				v-model="modelItem.price_adjustment_type"
				color="secondary"
				bg-color="surface"
				slider-color="primary"
				class="mb-2 rounded curve-tabs"
				centered
			>
				<v-tab value="relative" class="curve-tab" variant="flat">Relative</v-tab>
				<v-tab value="absolute" class="curve-tab" variant="flat">Absolute</v-tab>
			</v-tabs>

			<v-alert
				v-if="tabs.find((t) => t.value === modelItem.type)?.readonly"
				type="info"
				class="mt-3"
			>
				{{ t("pricelist.curves.modal.notice") }}
			</v-alert>
		</v-form>
	</Modal>
</template>

<style scoped lang="scss">
.color-preview {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.curve-preview-container {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: center;
	grid-template-columns: 1fr auto;
}

.curve-tabs {
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-radius: 8px;
	overflow: hidden;
}

.curve-tab {
	flex: 1;
	min-width: 0;
}

:deep(.v-stepper-header) {
	box-shadow: none !important;
}

:deep(.v-stepper-window) {
	min-height: 420px;
}

:deep(.v-tab--selected) {
	background-color: rgba(var(--v-theme-primary), 0.1);
}
</style>
